import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  FolderUp,
  ScanSearch,
  CalendarCheck2,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ResolveWordmark from "../components/ResolveWordmark";
import { pageEnter, EASE_OUT } from "../lib/motion";

const PROVIDER_ICONS = {
  entra: "M",
  google: "G",
  okta: "O",
  mock: "✦",
};

const PILLARS = [
  { icon: ClipboardList, label: "Case Management" },
  { icon: FolderUp, label: "Document Management" },
  { icon: ScanSearch, label: "Investigation Tracking" },
  { icon: CalendarCheck2, label: "Follow-up & Actions" },
  { icon: TrendingUp, label: "Reporting & Analytics" },
  { icon: ShieldCheck, label: "Integrity · Respect · Trust" },
];

export default function Login() {
  const { user, login, adoptSsoTokens, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ssoProviders, setSsoProviders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/sso/providers")
      .then(({ data }) => {
        if (!cancelled) setSsoProviders(data.providers || []);
      })
      .catch(() => {
        if (!cancelled) setSsoProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!window.location.hash || !window.location.hash.includes("sso=ok")) return;
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = hash.get("token");
    const refresh = hash.get("refresh");
    const next = hash.get("next") || "/cases";
    if (token) {
      adoptSsoTokens(token, refresh).then(() => {
        toast.success("Signed in via SSO");
        window.history.replaceState({}, "", "/login");
        navigate(next, { replace: true });
      });
    }
  }, [adoptSsoTokens, navigate]);

  if (!loading && user) {
    return <Navigate to="/cases" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const result = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error || "Login failed");
      toast.error(result.error || "Login failed");
      return;
    }
    if (result.mfa_required) {
      navigate("/mfa-verify", { state: { mfa_session: result.mfa_session, email } });
      return;
    }
    if (result.must_change_password) {
      toast.message("Password change required", {
        description: "An admin issued you a temporary password.",
      });
      navigate("/change-password", { replace: true });
      return;
    }
    toast.success("Welcome back");
    navigate("/cases", { replace: true });
  };

  const startSso = (providerName) => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/${providerName}/login?next=/cases`;
  };

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen lg:grid lg:grid-cols-[minmax(0,560px)_1fr]"
      style={{ backgroundColor: "var(--page-bg)" }}
    >
      {/* ===== FORM COLUMN ===== */}
      <motion.div
        {...pageEnter}
        className="relative flex min-h-screen flex-col px-6 py-12 sm:px-12 lg:px-16"
      >
        {/* small top brand lockup */}
        <div className="flex items-center gap-2.5">
          <img
            src="/brand/goodwin-resolve-logo.png"
            alt="Goodwin Resolve"
            className="h-9 w-9 object-contain"
            draggable={false}
            data-testid="login-brand-logo"
          />
          <div data-testid="login-brand-wordmark" className="leading-none">
            <ResolveWordmark size="sm" scheme="navy" />
            <p
              className="mt-1 text-[9px] font-medium uppercase tracking-[0.18em]"
              style={{ color: "var(--page-fg-muted)" }}
              data-testid="login-attribution"
            >
              by Goodwin Living
            </p>
          </div>
        </div>

        {/* form, centered vertically with breathing room */}
        <div className="my-auto w-full max-w-md">
          <p
            data-testid="login-tagline"
            className="text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            Resolve today. Strengthen tomorrow.
          </p>
          <h1
            className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            Welcome back
          </h1>
          <p
            className="mt-2 text-sm"
            style={{ color: "var(--page-fg-secondary)" }}
          >
            Sign in to continue to your Employee Relations workspace.
          </p>

          {ssoProviders.length > 0 && (
            <div data-testid="sso-providers" className="mt-8 space-y-2">
              {ssoProviders.map((p) => (
                <Button
                  key={p.name}
                  type="button"
                  variant="outline"
                  data-testid={`sso-button-${p.name}`}
                  onClick={() => startSso(p.name)}
                  className="hairline h-11 w-full justify-start rounded-lg bg-transparent text-sm font-medium transition hover:bg-[color:var(--surface-2)] dark:hover:bg-white/5"
                  style={{ borderColor: "var(--border-strong)", color: "var(--page-fg)" }}
                >
                  <span
                    className="mr-3 flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-bold text-white"
                    style={{ backgroundColor: "#1E3A5F" }}
                  >
                    {PROVIDER_ICONS[p.name] || "•"}
                  </span>
                  Continue with {p.label}
                </Button>
              ))}
              <div
                className="flex items-center gap-3 pt-3 text-[10px] uppercase tracking-[0.18em]"
                style={{ color: "var(--page-fg-muted)" }}
              >
                <span className="h-px flex-1" style={{ backgroundColor: "var(--border-subtle)" }} />
                <span>or with email</span>
                <span className="h-px flex-1" style={{ backgroundColor: "var(--border-subtle)" }} />
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="mt-6 space-y-4"
            data-testid="login-form"
          >
            <div className="space-y-1.5">
              <Label
                htmlFor="email"
                className="text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                Email address
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@goodwinliving.org"
                className="h-11 rounded-lg text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="password"
                className="text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 rounded-lg text-sm"
              />
            </div>
            {error && (
              <p
                data-testid="login-error"
                className="rounded-lg border px-3 py-2 text-xs"
                style={{
                  borderColor: "rgba(229,72,77,0.3)",
                  backgroundColor: "rgba(229,72,77,0.06)",
                  color: "#B42318",
                }}
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="h-11 w-full rounded-lg text-sm font-semibold text-white transition active:scale-[0.99]"
              style={{
                background: "linear-gradient(180deg, #234876 0%, #1E3A5F 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.10), 0 1px 0 rgba(15,15,20,0.04)",
              }}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            <div
              className="flex items-center justify-between pt-1 text-xs"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              <Link
                to="/register"
                className="font-medium transition hover:underline"
                data-testid="login-register-link"
                style={{ color: "var(--page-fg)" }}
              >
                Request access
              </Link>
              <Link
                to="/forgot-password"
                className="font-medium transition hover:underline"
                data-testid="login-forgot-link"
                style={{ color: "var(--page-fg)" }}
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </div>

        {/* footer pillars — compact */}
        <div
          className="mt-12 grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-6"
          data-testid="login-pillars"
        >
          {PILLARS.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-start gap-1.5"
              data-testid={`login-pillar-${label.replace(/\W+/g, "-").toLowerCase()}`}
            >
              <Icon
                className="h-4 w-4"
                strokeWidth={1.5}
                style={{ color: "var(--page-fg-secondary)" }}
              />
              <span
                className="text-[9px] font-semibold uppercase leading-tight tracking-[0.10em]"
                style={{ color: "var(--page-fg-muted)" }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        <div
          className="mt-8 flex flex-wrap items-center justify-between gap-2 text-[11px]"
          style={{ color: "var(--page-fg-muted)" }}
          data-testid="login-page-footer"
        >
          <span>© {new Date().getFullYear()} Goodwin Living · Goodwin Resolve</span>
          <Link
            to="/accessibility-statement"
            data-testid="login-accessibility-link"
            className="font-medium transition-colors hover:text-[color:var(--page-fg)] focus-visible:underline"
          >
            Accessibility
          </Link>
        </div>
      </motion.div>

      {/* ===== HERO COLUMN (≥ lg) ===== */}
      <motion.aside
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1, ease: EASE_OUT }}
        aria-hidden="true"
        className="relative hidden overflow-hidden lg:block"
        style={{
          backgroundColor: "#0E1F3A",
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(94, 171, 69, 0.20) 0%, transparent 45%),
            radial-gradient(circle at 80% 70%, rgba(30, 58, 95, 0.55) 0%, transparent 55%),
            radial-gradient(circle at 50% 100%, rgba(94, 171, 69, 0.10) 0%, transparent 60%)
          `,
        }}
      >
        {/* subtle dot grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        {/* large abstract shape */}
        <div
          aria-hidden
          className="absolute -right-24 -top-24 h-[520px] w-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(94,171,69,0.30) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-32 -left-24 h-[480px] w-[480px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(94,171,69,0.18) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        {/* editorial copy */}
        <div className="relative flex h-full flex-col justify-center p-16 text-white">
          <h2
            className="font-serif text-4xl font-semibold xl:text-5xl"
            style={{ letterSpacing: "-0.02em", color: "#F4F4F5" }}
          >
            Employee Relations,
            <br />
            <span style={{ color: "#84C170" }}>handled with care.</span>
          </h2>
          <p
            className="mt-6 max-w-md text-sm leading-relaxed"
            style={{ color: "rgba(255,255,255,0.74)" }}
          >
            Goodwin Resolve unifies investigations, witness interviews, document
            management, and audit-grade reporting on one calm canvas — built so
            HR teams can be present when it matters.
          </p>
        </div>
      </motion.aside>
    </div>
  );
}
